/*
  MASTER — formateo de dinero.

  Una sola fuente de verdad para presentar importes, compartida por todo el JS
  del theme.

  El problema que resuelve: el servidor formatea con el filtro `money` de Liquid,
  que usa la cadena `shop.money_format` de la tienda, y el cliente formateaba con
  `Intl.NumberFormat(document.documentElement.lang, {currency})`. No coinciden.

  En esta tienda `<html lang>` vale `es` (sale de `request.locale.iso_code`, no
  de la región), así que para Intl el COP es una divisa extranjera en español
  genérico: emite `3995 COP` —sin separador de millares por debajo de 10.000 y
  con el código en vez del símbolo— mientras Liquid emite `$3.995`. El mismo
  valor se veía de dos formas según lo hubiera pintado el servidor o una
  actualización de carrito.

  La solución no es adivinar mejor el locale: es usar exactamente la misma
  cadena de formato que usa Liquid. Así servidor y cliente coinciden por
  construcción, en cualquier tienda y divisa, sin tabla de locales que mantener.

  Sólo cambia la PRESENTACIÓN. El valor en céntimos que manda Shopify no se toca.
*/
(() => {
  if (window.MasterMoney) return;

  const DEFAULT_FORMAT = '${{amount}}';

  /* Separador de millares cada tres dígitos, sin tocar la parte decimal. */
  const groupThousands = (digits, separator) =>
    digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  /*
    Siempre en valor absoluto. El signo lo pone `format` por fuera de la
    plantilla: metiéndolo aquí salía "$-3.995", porque el símbolo de moneda vive
    en la cadena de Shopify y el menos quedaba detrás. Lo correcto es "-$3.995".

    `toFixed` redondea, que es lo que hace la implementación de referencia de
    Shopify para los placeholders sin decimales. Se mantiene para no presentar
    una cifra distinta de la que emite Liquid en el mismo carrito.
  */
  /*
    «Ocultar ceros finales» es la MISMA regla que aplica el servidor con el
    filtro `money_without_trailing_zeros` de Shopify: se omite la parte decimal
    sólo cuando es toda ceros, y se respeta intacta cuando no lo es. Así
    79.900,00 se lee 79.900 y 69.966,67 sigue entero.

    No es un redondeo. `cents` no se toca, y la parte entera se construye igual
    que antes: lo único que cambia es si se imprimen unos decimales que no
    aportan información.
  */
  const hideZeroDecimals = () => window.MasterTheme?.hideZeroDecimals === true;

  const build = (cents, {decimals, thousands, decimal}) => {
    const value = Number(cents);
    const safe = Number.isFinite(value) ? Math.abs(value) : 0;
    const fixed = (safe / 100).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    let out = groupThousands(whole, thousands);
    const allZeros = Boolean(fraction) && /^0+$/.test(fraction);
    if (decimals > 0 && fraction && !(hideZeroDecimals() && allZeros)) out += decimal + fraction;
    return out;
  };

  /*
    Placeholders de Shopify. Cada uno fija su propia pareja de separadores y si
    lleva decimales; por eso se resuelven por tabla y no por heurística.
  */
  const RULES = {
    amount: {decimals: 2, thousands: ',', decimal: '.'},
    amount_no_decimals: {decimals: 0, thousands: ',', decimal: '.'},
    amount_with_comma_separator: {decimals: 2, thousands: '.', decimal: ','},
    amount_no_decimals_with_comma_separator: {decimals: 0, thousands: '.', decimal: ','},
    amount_with_apostrophe_separator: {decimals: 2, thousands: "'", decimal: '.'},
    amount_no_decimals_with_apostrophe_separator: {decimals: 0, thousands: "'", decimal: '.'},
    amount_with_space_separator: {decimals: 2, thousands: ' ', decimal: ','},
    amount_no_decimals_with_space_separator: {decimals: 0, thousands: ' ', decimal: ','},
    amount_with_period_and_space_separator: {decimals: 2, thousands: ' ', decimal: '.'}
  };

  /*
    Reserva: si la tienda no expuso su formato, se usa el locale como antes.
    Es peor que la cadena real, pero nunca deja un importe sin pintar.
  */
  const intlFallback = cents => {
    const value = (Number(cents) || 0) / 100;
    /* Sin divisa conocida no se inventa una: `style:'currency'` sin `currency`
       lanza, y afirmar COP en una tienda que vende en MXN sería peor que
       imprimir el número pelado. */
    const currency = (window.MasterTheme && window.MasterTheme.currency)
      || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined,
        currency
          ? {style: 'currency', currency, maximumFractionDigits: 0}
          : {maximumFractionDigits: 0}
      ).format(value);
    } catch (_) {
      return String(Math.round(value));
    }
  };

  const format = (cents, formatString) => {
    const template = String(
      formatString || (window.MasterTheme && window.MasterTheme.moneyFormat) || ''
    ).trim() || DEFAULT_FORMAT;

    let matched = false;
    const out = template.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, name) => {
      const rule = RULES[name];
      if (!rule) return whole;
      matched = true;
      return build(cents, rule);
    });

    /*
      Una cadena sin ningún placeholder conocido significa que no la entendemos;
      devolver el literal dejaría el precio sin cifra. Mejor la reserva.
    */
    if (!matched) return intlFallback(cents);
    const value = Number(cents);
    return (Number.isFinite(value) && value < 0 ? '-' : '') + out;
  };

  window.MasterMoney = {format, intlFallback};
})();
